import React, { useState, useEffect, useCallback } from "react"
import { createStyles, styled, forwardRef } from "@design-system/core"
import { SwitchProps, SwitchStatus } from "./types"

export const Switch = forwardRef<SwitchProps, "input">((props, ref) => {
  const { status, defaultStatus, onStatusChange, ...others } = props
  const [switchState, setSwitchState] = useState<SwitchStatus>(
    status ?? defaultStatus ?? "off",
  )

  useEffect(() => {
    if (switchState !== status) {
      setSwitchState(status ?? switchState)
    }
  }, [status, switchState])

  const handleSwitchChange = useCallback(() => {
    let newState: SwitchStatus
    setSwitchState((state) => {
      newState = state === "on" ? "off" : "on"
      onStatusChange?.(newState)
      return newState
    })
  }, [switchState, onStatusChange])

  return (
    <styled.label
      style={[styles.disabledTrack, switchState === "on" && styles.track]}
    >
      <input
        ref={ref}
        style={styles.input}
        type="checkbox"
        checked={switchState === "on"}
        onChange={handleSwitchChange}
      />
      <styled.span
        style={[
          styles.animationBefore,
          styles.disabledKnob,
          switchState === "on" && styles.knob && styles.animation,
        ]}
      />
    </styled.label>
  )
})

const styles = createStyles((theme) => ({
  knob: {
    borderColor: theme.borderColors.action,
  },
  disabledKnob: {
    top: 1,
    width: 21,
    height: 22,
    borderRadius: theme.borderRadii.full,
    borderStyle: "solid",
    position: "absolute",
    borderWidth: theme.borderWidths.small,
    borderColor: theme.borderColors.disabled,
    backgroundColor: theme.backgroundColors.default,
  },
  animationBefore: {
    left: "0px",
    transitionProperty: "all",
    transitionDuration: "250ms",
    transitionTimingFunction: "ease-in-out",
  },
  animation: {
    left: theme.spaces.spacing16,
  },
  track: {
    backgroundColor: theme.backgroundColors.action,
  },
  disabledTrack: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: 38,
    height: 24,
    borderRadius: theme.borderRadii.full,
    position: "relative",
    display: "inline-block",
    backgroundColor: theme.colors.gray200,
  },
  input: {
    width: 0,
    height: 0,
    opacity: 0,
  },
}))
